import React, { useCallback, useEffect, useState } from "react";
import { Bot, ExternalLink, RefreshCw, Wrench, Zap } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { fetchAgents } from "../../../services/api";
import styles from "./PainelAgentes.module.css";

// status: 'checking' | 'online' | 'offline' | 'unconfigured'

function StatusDot({ status }) {
    return (
        <span
            className={[
                styles.statusDot,
                status === "online"       ? styles.dotOnline
                : status === "offline"   ? styles.dotOffline
                : status === "checking"  ? styles.dotChecking
                : styles.dotUnconfigured,
            ].join(" ")}
        />
    );
}

function StatusPill({ status }) {
    const LABELS = {
        online:        "Online",
        offline:       "Offline",
        checking:      "Verificando…",
        unconfigured:  "Não configurado",
    };
    return (
        <span
            className={[
                styles.statusPill,
                status === "online"       ? styles.pillOnline
                : status === "offline"   ? styles.pillOffline
                : status === "checking"  ? styles.pillChecking
                : styles.pillUnconfigured,
            ].join(" ")}
        >
            <StatusDot status={status} />
            {LABELS[status] ?? status}
        </span>
    );
}

function formatCheckedAt(date) {
    if (!date) return "Nunca";
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 5) return "Agora mesmo";
    if (diff < 60) return `Há ${diff}s`;
    return `Há ${Math.floor(diff / 60)}min`;
}

export default function PainelAgentes() {
    const { password } = useAuth();

    const [agents, setAgents]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState("");
    const [refreshing, setRefreshing] = useState(false);

    // Per-agent health status and last-checked time
    const [statuses, setStatuses]   = useState({});   // { [id]: status }
    const [checkedAt, setCheckedAt] = useState({});   // { [id]: Date }

    // ── Health check (browser-side, CORS open on MCP server) ─────────────────

    const checkHealth = useCallback(async (agent) => {
        if (!agent.health_url) {
            setStatuses((prev) => ({ ...prev, [agent.id]: "unconfigured" }));
            return;
        }
        setStatuses((prev) => ({ ...prev, [agent.id]: "checking" }));
        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 6000);
            const res = await fetch(agent.health_url, { signal: controller.signal });
            clearTimeout(timer);
            setStatuses((prev) => ({
                ...prev,
                [agent.id]: res.ok ? "online" : "offline",
            }));
        } catch {
            setStatuses((prev) => ({ ...prev, [agent.id]: "offline" }));
        }
        setCheckedAt((prev) => ({ ...prev, [agent.id]: new Date() }));
    }, []);

    // ── Load agents list ──────────────────────────────────────────────────────

    const loadAgents = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchAgents(password);
            setAgents(data.agents || []);
        } catch (err) {
            setError(err.message || "Erro ao carregar agentes.");
        } finally {
            setLoading(false);
        }
    }, [password]);

    useEffect(() => { loadAgents(); }, [loadAgents]);

    // Run health checks whenever the agent list changes
    useEffect(() => {
        agents.forEach((agent) => checkHealth(agent));
    }, [agents, checkHealth]);

    // ── Actions ───────────────────────────────────────────────────────────────

    const handleRefreshAll = async () => {
        setRefreshing(true);
        await loadAgents();
        setRefreshing(false);
    };

    // ── Derived stats ─────────────────────────────────────────────────────────

    const onlineCount  = Object.values(statuses).filter((s) => s === "online").length;
    const offlineCount = Object.values(statuses).filter((s) => s === "offline").length;
    const totalTools   = agents.reduce((sum, a) => sum + (a.tools?.length ?? 0), 0);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className={styles.page}>

            {/* Topbar */}
            <div className={styles.topbar}>
                <div>
                    <span className={styles.eyebrow}>Agentes IA</span>
                    <h1>Agentes Conectados</h1>
                    <p>
                        Monitore os agentes de inteligência artificial integrados ao
                        painel. Cada agente possui ferramentas específicas para
                        automatizar tarefas.
                    </p>
                </div>
                <div className={styles.topbarActions}>
                    <button
                        className={styles.secondaryButton}
                        onClick={handleRefreshAll}
                        disabled={loading || refreshing}
                    >
                        <span className={refreshing ? styles.spinning : undefined}>
                            <RefreshCw size={15} />
                        </span>
                        {refreshing ? "Atualizando…" : "Atualizar"}
                    </button>
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <div className={styles.errorMessage}>
                    <strong>⚠</strong>
                    <span>{error}</span>
                </div>
            )}

            {/* Stats */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <span>Total de agentes</span>
                    <strong>{agents.length}</strong>
                </div>
                <div className={styles.statCard}>
                    <span>Online</span>
                    <strong className={onlineCount > 0 ? styles.statOnline : undefined}>
                        {onlineCount}
                    </strong>
                </div>
                <div className={styles.statCard}>
                    <span>Offline</span>
                    <strong className={offlineCount > 0 ? styles.statOffline : undefined}>
                        {offlineCount}
                    </strong>
                </div>
                <div className={styles.statCard}>
                    <span>Ferramentas</span>
                    <strong>{totalTools}</strong>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className={styles.loadingState}>
                    <span className={styles.spinner} />
                    <span>Carregando agentes…</span>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && agents.length === 0 && (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <Bot size={26} />
                    </div>
                    <h2>Nenhum agente configurado</h2>
                    <p>
                        Configure a variável <code>MCP_AGENT_URL</code> no servidor
                        Flask para registrar o primeiro agente.
                    </p>
                </div>
            )}

            {/* Agents grid */}
            {!loading && agents.length > 0 && (
                <div className={styles.agentsGrid}>
                    {agents.map((agent) => {
                        const status  = statuses[agent.id] ?? "checking";
                        const checked = checkedAt[agent.id];

                        return (
                            <div key={agent.id} className={styles.agentCard}>

                                {/* Header */}
                                <div className={styles.cardHeader}>
                                    <div className={styles.cardHeaderLeft}>
                                        <div className={styles.agentIconWrap}>
                                            <Bot size={22} />
                                        </div>
                                        <div>
                                            <div className={styles.agentName}>{agent.name}</div>
                                            <div className={styles.agentBadges}>
                                                <span className={styles.badge}>{agent.type}</span>
                                                <span className={styles.badgeClient}>{agent.client}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <StatusPill status={status} />
                                </div>

                                {/* Description */}
                                <p className={styles.agentDescription}>{agent.description}</p>

                                {/* Endpoint URL */}
                                {agent.mcp_endpoint && (
                                    <div className={styles.endpointRow}>
                                        <Zap size={13} />
                                        <span className={styles.endpointUrl}>{agent.mcp_endpoint}</span>
                                        <a
                                            href={agent.mcp_endpoint}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={styles.externalLink}
                                            title="Abrir endpoint MCP"
                                        >
                                            <ExternalLink size={12} />
                                        </a>
                                    </div>
                                )}

                                {/* Divider */}
                                <div className={styles.divider} />

                                {/* Tools */}
                                <div className={styles.toolsSection}>
                                    <div className={styles.toolsHeader}>
                                        <Wrench size={13} />
                                        <span>
                                            {agent.tools?.length ?? 0}&nbsp;
                                            {(agent.tools?.length ?? 0) === 1 ? "ferramenta" : "ferramentas"}
                                        </span>
                                    </div>
                                    <div className={styles.toolsGrid}>
                                        {(agent.tools ?? []).map((tool) => (
                                            <span
                                                key={tool.name}
                                                className={styles.toolChip}
                                                title={tool.description}
                                            >
                                                {tool.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className={styles.cardFooter}>
                                    <span className={styles.checkedAt}>
                                        Verificado: {formatCheckedAt(checked)}
                                    </span>
                                    <button
                                        className={styles.refreshButton}
                                        onClick={() => checkHealth(agent)}
                                        disabled={status === "checking"}
                                        title="Testar conexão agora"
                                    >
                                        <RefreshCw size={13} />
                                        Verificar
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            )}

        </div>
    );
}
